import { useState } from "react";
import { useZero } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

/** The Zero client, typed for running custom mutators. */
export function useZeroClient() {
  return useZero() as unknown as ZeroMutateClient;
}

/** Runs a mutator and throws if the server rejects it. */
export async function runZeroMutation(
  zero: ZeroMutateClient,
  mutation: unknown,
  fallbackError: string,
) {
  const result = zero.mutate(mutation);
  await result.client;
  const server = await result.server;
  if (server.type === "error") {
    throw new Error(server.error?.message || fallbackError);
  }
}

/**
 * Wraps an async operation with pending state and success/error toasts.
 * `mutate` reports errors as toasts; `mutateAsync` rethrows them.
 */
export function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}
